import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  ScaleFade,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
} from '@chakra-ui/react';
import { RiCloseLine, RiFileExcel2Line } from 'react-icons/ri';
import { endOfMonth, format, startOfMonth } from 'date-fns';

import { HeaderUp } from '../../../../components/_components_0.2/Header_0.2';
import { Sidebar } from '../../../../components/_components_0.2/Sidebar_0.2';
import { apllyToast } from '../../../../components/Global/Toast2.0';
import { expenseReportsService } from '../../../../services/expenseReportsService';
import { useUsers } from '../../../../hooks/useUsers';
import type { IExpenseReport } from '../Expenses';

import { KmCharts } from './KmCharts';
import { KmTable } from './KmTable';
import {
  IKmFilters,
  KM_STATUS_OPTIONS,
  KmStatus,
  buildKmEntries,
  downloadKmCsv,
  filterKmEntries,
  formatCurrency,
  formatKm,
  uniqueOptions,
} from './kmData';

const defaultFilters = (): IKmFilters => ({
  dateFrom: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  dateTo: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  userId: '',
  section: '',
  costCenter: '',
  statuses: KM_STATUS_OPTIONS.map(s => s.value),
});

const fieldProps = {
  size: 'sm',
  bg: 'whiteAlpha.100',
  borderColor: 'whiteAlpha.300',
  color: 'gray.200',
} as const;

const optionStyle = { background: '#fff', color: '#1a202c' };

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <Stat
      p="4"
      bg="whiteAlpha.50"
      borderRadius="md"
      border="1px solid"
      borderColor="whiteAlpha.200"
    >
      <StatLabel color="gray.400">{label}</StatLabel>
      <StatNumber color="gray.100" fontSize="2xl">
        {value}
      </StatNumber>
    </Stat>
  );
}

export default function KmControl() {
  const { getUserName } = useUsers();
  const [reports, setReports] = useState<IExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<IKmFilters>(defaultFilters);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await expenseReportsService.list({
        page: 1,
        take: 1000,
        showConsolidated: true,
      });
      setReports(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      apllyToast('error', 'Erro ao carregar os KMs dos relatórios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const allEntries = useMemo(
    () => buildKmEntries(reports, getUserName),
    // getUserName muda a cada render; os nomes só mudam quando os relatórios mudam
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reports],
  );

  const entries = useMemo(
    () => filterKmEntries(allEntries, filters),
    [allEntries, filters],
  );

  const userOptions = useMemo(() => {
    const names = new Map<string, string>();
    allEntries.forEach(e => names.set(e.userId, e.userName));
    return Array.from(names, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'pt-BR'),
    );
  }, [allEntries]);

  const sectionOptions = useMemo(
    () => uniqueOptions(allEntries, e => e.sectionLabel),
    [allEntries],
  );

  const costCenterOptions = useMemo(
    () => uniqueOptions(allEntries, e => e.costCenterLabel),
    [allEntries],
  );

  const totals = useMemo(() => {
    const km = entries.reduce((sum, e) => sum + e.km, 0);
    const amount = entries.reduce((sum, e) => sum + e.amount, 0);
    return {
      km,
      amount,
      count: entries.length,
      average: entries.length ? km / entries.length : 0,
    };
  }, [entries]);

  const setFilter = <K extends keyof IKmFilters>(
    key: K,
    value: IKmFilters[K],
  ) => setFilters(prev => ({ ...prev, [key]: value }));

  const handleStatusChange = (values: (string | number)[]) =>
    setFilter('statuses', values as KmStatus[]);

  const handleExport = () => {
    const period = `${filters.dateFrom || 'inicio'}_a_${
      filters.dateTo || 'hoje'
    }`;
    downloadKmCsv(entries, `controle-km_${period}.csv`);
  };

  return (
    <Flex direction="column" h="100vh">
      <HeaderUp path="expenses" />

      <Flex w="100%" my="6" maxWidth={1480} mx="auto" pb={4} px="6">
        <Sidebar path="expenses" />
        <Box flex="1" minW="0">
          <ScaleFade initialScale={0.9} in>
            <Box borderRadius={8} bg="gray.800" p={['4', '8']}>
              <Flex
                mb="6"
                justify="space-between"
                align={['flex-start', 'center']}
                direction={['column', 'row']}
                gap="3"
              >
                <Box>
                  <Heading size="lg" fontWeight="normal">
                    Controle de KM
                  </Heading>
                  <Text fontSize="sm" color="gray.400">
                    KM percorridos nos relatórios de despesa (recusados não
                    entram).
                  </Text>
                </Box>

                <Button
                  size="sm"
                  colorScheme="green"
                  leftIcon={<Icon as={RiFileExcel2Line} fontSize="18" />}
                  onClick={handleExport}
                  isDisabled={loading || entries.length === 0}
                >
                  Exportar para Excel
                </Button>
              </Flex>

              {/* Filtros */}
              <Box
                mb="6"
                p="4"
                bg="whiteAlpha.100"
                borderRadius="md"
                border="1px solid"
                borderColor="whiteAlpha.200"
              >
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="bold" color="gray.300">
                    Filtros
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    leftIcon={<Icon as={RiCloseLine} />}
                    onClick={() => setFilters(defaultFilters())}
                  >
                    Limpar filtros
                  </Button>
                </Flex>

                <Flex gap={3} flexWrap="wrap">
                  <Box minW="140px" flex={1}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Data de
                    </Text>
                    <Input
                      {...fieldProps}
                      type="date"
                      value={filters.dateFrom}
                      onChange={e => setFilter('dateFrom', e.target.value)}
                    />
                  </Box>
                  <Box minW="140px" flex={1}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Data até
                    </Text>
                    <Input
                      {...fieldProps}
                      type="date"
                      value={filters.dateTo}
                      onChange={e => setFilter('dateTo', e.target.value)}
                    />
                  </Box>

                  <Box minW="200px" flex={1}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Usuário
                    </Text>
                    <Select
                      {...fieldProps}
                      value={filters.userId}
                      onChange={e => setFilter('userId', e.target.value)}
                    >
                      <option value="" style={optionStyle}>
                        Todos
                      </option>
                      {userOptions.map(u => (
                        <option key={u.id} value={u.id} style={optionStyle}>
                          {u.name}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box minW="200px" flex={1}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Seção
                    </Text>
                    <Select
                      {...fieldProps}
                      value={filters.section}
                      onChange={e => setFilter('section', e.target.value)}
                    >
                      <option value="" style={optionStyle}>
                        Todas
                      </option>
                      {sectionOptions.map(option => (
                        <option key={option} value={option} style={optionStyle}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box minW="200px" flex={1}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Centro de custo
                    </Text>
                    <Select
                      {...fieldProps}
                      value={filters.costCenter}
                      onChange={e => setFilter('costCenter', e.target.value)}
                    >
                      <option value="" style={optionStyle}>
                        Todos
                      </option>
                      {costCenterOptions.map(option => (
                        <option key={option} value={option} style={optionStyle}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  </Box>
                </Flex>

                <Box mt={3}>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Status
                  </Text>
                  <CheckboxGroup
                    colorScheme="blue"
                    value={filters.statuses}
                    onChange={handleStatusChange}
                  >
                    <HStack spacing={6} flexWrap="wrap">
                      {KM_STATUS_OPTIONS.map(option => (
                        <Checkbox
                          key={option.value}
                          value={option.value}
                          size="sm"
                          color="gray.200"
                        >
                          {option.label}
                        </Checkbox>
                      ))}
                    </HStack>
                  </CheckboxGroup>
                </Box>
              </Box>

              {loading ? (
                <Center h="300px">
                  <Spinner size="xl" color="blue.500" thickness="4px" />
                </Center>
              ) : (
                <>
                  <SimpleGrid columns={[1, 2, 4]} spacing="4" mb="6">
                    <SummaryCard label="KM total" value={formatKm(totals.km)} />
                    <SummaryCard
                      label="Valor em KM"
                      value={formatCurrency(totals.amount)}
                    />
                    <SummaryCard
                      label="Deslocamentos"
                      value={totals.count.toLocaleString('pt-BR')}
                    />
                    <SummaryCard
                      label="Média por deslocamento"
                      value={formatKm(totals.average)}
                    />
                  </SimpleGrid>

                  <KmCharts
                    entries={entries}
                    dateFrom={filters.dateFrom}
                    dateTo={filters.dateTo}
                  />

                  <Text fontSize="sm" fontWeight="bold" color="gray.300" mb="2">
                    Deslocamentos
                  </Text>
                  <KmTable entries={entries} />
                </>
              )}
            </Box>
          </ScaleFade>
        </Box>
      </Flex>
    </Flex>
  );
}
